import React, { useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useDisclosure,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';

interface LogoDeleteConfirmDialogProps {
  isBusy: boolean;
  isDeleting: boolean;
  logoTitle: string;
  currentLogoUrl?: string;
  onConfirmDelete: () => void;
  onTriggerClick?: () => void;
}

export const LogoDeleteConfirmDialog: React.FC<LogoDeleteConfirmDialogProps> = React.memo(
  ({ isBusy, isDeleting, currentLogoUrl, logoTitle, onConfirmDelete, onTriggerClick }) => {
    const { t } = useTranslation('uploadCompanyLogo');
    const cancelDeleteButtonRef = useRef<HTMLButtonElement>(null);

    const { isOpen: isDeleteConfirmOpen, onOpen: openDeleteConfirm, onClose: closeDeleteConfirm } = useDisclosure();

    const handleTriggerClick = useCallback(() => {
      onTriggerClick?.();
      openDeleteConfirm();
    }, [onTriggerClick, openDeleteConfirm]);

    const handleConfirmDeleteClick = useCallback(() => {
      closeDeleteConfirm();
      onConfirmDelete();
    }, [closeDeleteConfirm, onConfirmDelete]);

    return (
      <>
        <Button
          variant="outline"
          colorScheme="red"
          onClick={handleTriggerClick}
          isLoading={isDeleting}
          loadingText={t('deleteButton.loadingText')}
          isDisabled={!currentLogoUrl || isBusy}
        >
          {t('deleteButton.label')}
        </Button>
        <AlertDialog isOpen={isDeleteConfirmOpen} leastDestructiveRef={cancelDeleteButtonRef} onClose={closeDeleteConfirm}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t('deleteButton.alertHeader', { logoTitle })}
              </AlertDialogHeader>

              <AlertDialogBody>{t('deleteButton.alertBody', { logoTitle: logoTitle.toLowerCase() })}</AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelDeleteButtonRef} onClick={closeDeleteConfirm}>
                  {t('deleteButton.alertCancel')}
                </Button>
                <Button colorScheme="red" onClick={handleConfirmDeleteClick} ml={3}>
                  {t('deleteButton.alertConfirm')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </>
    );
  },
);
LogoDeleteConfirmDialog.displayName = 'LogoDeleteConfirmDialog';

export default LogoDeleteConfirmDialog;
